import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Calculator, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Zap,
  Activity
} from 'lucide-react';
import { useCompensationRules, usePayrollPreview } from '@/hooks/compensation';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { CompensationRule } from '@/services/compensation/types';

interface RealTimeCalculationEngineProps {
  selectedEmployeeIds?: string[];
  startDate: Date;
  endDate: Date;
  onCalculationComplete?: (results: any) => void;
  autoRefresh?: boolean;
  refreshInterval?: number; // in seconds
}

interface CalculationStatus {
  status: 'idle' | 'calculating' | 'completed' | 'error';
  progress: number;
  message: string;
  warnings: string[];
  errors: string[];
  lastCalculated?: Date;
}

// Minimum refresh interval to prevent excessive API calls (in seconds)
const MIN_REFRESH_INTERVAL = 30;
const DEFAULT_REFRESH_INTERVAL = 60;

export function RealTimeCalculationEngine({
  selectedEmployeeIds = [],
  startDate,
  endDate,
  onCalculationComplete,
  autoRefresh = false,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
}: RealTimeCalculationEngineProps) {
  const [calculationStatus, setCalculationStatus] = useState<CalculationStatus>({
    status: 'idle',
    progress: 0,
    message: 'Ready to calculate',
    warnings: [],
    errors: []
  });

  const { currencyCode } = useWorkplaceCurrency();
  const { data: employees } = useCompanyEmployees();
  const employeeIds = selectedEmployeeIds.length > 0 
    ? selectedEmployeeIds 
    : employees?.map(e => e.id) || [];

  const { 
    data: payrollResults,
    isLoading,
    error,
    refetch 
  } = usePayrollPreview(employeeIds, startDate, endDate, true);

  // Enforce minimum interval cap to prevent excessive API calls
  const safeInterval = Math.max(refreshInterval, MIN_REFRESH_INTERVAL);

  // Auto-refresh functionality with enforced minimum interval
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      handleCalculation();
    }, safeInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, safeInterval, employeeIds, startDate, endDate]);

  // Update calculation status based on query state
  useEffect(() => {
    if (isLoading) {
      setCalculationStatus(prev => ({
        ...prev,
        status: 'calculating',
        progress: 50,
        message: 'Processing payroll calculations...'
      }));
    } else if (error) {
      setCalculationStatus(prev => ({
        ...prev,
        status: 'error',
        progress: 0,
        message: 'Calculation failed',
        errors: [error.message || 'Unknown error occurred']
      }));
    } else if (payrollResults) {
      const warnings = validateCalculationResults(payrollResults);
      setCalculationStatus(prev => ({
        ...prev,
        status: 'completed',
        progress: 100,
        message: `Calculated for ${payrollResults.length} employees`,
        warnings,
        errors: [],
        lastCalculated: new Date()
      }));
      
      if (onCalculationComplete) {
        onCalculationComplete(payrollResults);
      }
    }
  }, [isLoading, error, payrollResults, onCalculationComplete]);

  const validateCalculationResults = (results: any[]): string[] => {
    const warnings: string[] = [];
    
    results.forEach(result => {
      // Check for zero pay
      if (result.calculation.netPay <= 0) {
        warnings.push(`${result.employeeName} has zero or negative net pay`);
      }
      
      // Check for missing time entries
      if (!result.timeEntries || result.timeEntries.length === 0) {
        warnings.push(`${result.employeeName} has no time entries for this period`);
      }
      
      // Check for excessive overtime
      const overtimeHours = result.calculation.calculationSummary.overtimeHours || 0;
      if (overtimeHours > 20) {
        warnings.push(`${result.employeeName} has ${overtimeHours} overtime hours`);
      }
      
      // Check for missing compensation rules
      if (result.calculation.lineItems.length === 0) {
        warnings.push(`${result.employeeName} has no active compensation rules`);
      }
    });
    
    return warnings;
  };

  const handleCalculation = useCallback(async () => {
    setCalculationStatus(prev => ({
      ...prev,
      status: 'calculating',
      progress: 10,
      message: 'Starting calculation...',
      warnings: [],
      errors: []
    }));

    try {
      // Simulate calculation steps
      setCalculationStatus(prev => ({ ...prev, progress: 30, message: 'Validating compensation rules...' }));
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setCalculationStatus(prev => ({ ...prev, progress: 60, message: 'Processing time entries...' }));
      
      await refetch();
    } catch (error) {
      setCalculationStatus(prev => ({
        ...prev,
        status: 'error',
        progress: 0,
        message: 'Calculation failed',
        errors: [(error as Error).message || 'Unknown error']
      }));
    }
  }, [refetch]);

  const getStatusIcon = () => {
    switch (calculationStatus.status) {
      case 'calculating':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Calculator className="h-4 w-4" />;
    }
  };

  const getStatusColor = () => {
    switch (calculationStatus.status) {
      case 'calculating':
        return 'border-blue-200 bg-blue-50';
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  return (
    <Card className={`${getStatusColor()} transition-colors duration-300`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            Real-Time Calculation Engine
          </div>
          
          <div className="flex items-center gap-2">
            {autoRefresh && (
              <Badge variant="outline" className="text-xs">
                <Activity className="h-3 w-3 mr-1" />
                Auto-refresh: {refreshInterval}s
              </Badge>
            )}
            
            <Badge variant={calculationStatus.status === 'completed' ? 'default' : 'secondary'}>
              {calculationStatus.status.toUpperCase()}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Bar */}
        {calculationStatus.status === 'calculating' && (
          <div className="space-y-2">
            <Progress value={calculationStatus.progress} className="h-2" />
            <p className="text-sm text-muted-foreground">{calculationStatus.message}</p>
          </div>
        )}

        {/* Status Message */}
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{calculationStatus.message}</p>
          
          {calculationStatus.lastCalculated && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              Last: {calculationStatus.lastCalculated.toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* Manual Calculation Button */}
        <Button 
          onClick={handleCalculation}
          disabled={calculationStatus.status === 'calculating'}
          size="sm"
          className="w-full"
        >
          {calculationStatus.status === 'calculating' ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4 mr-2" />
              Recalculate Now
            </>
          )}
        </Button>

        {/* Warnings */}
        {calculationStatus.warnings.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Calculation Warnings:</p>
                {calculationStatus.warnings.map((warning, index) => (
                  <p key={index} className="text-sm">• {warning}</p>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Errors */}
        {calculationStatus.errors.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Calculation Errors:</p>
                {calculationStatus.errors.map((error, index) => (
                  <p key={index} className="text-sm">• {error}</p>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Summary Stats */}
        {payrollResults && calculationStatus.status === 'completed' && (
          <div className="grid grid-cols-3 gap-4 pt-2 border-t">
            <div className="text-center">
              <p className="text-lg font-semibold">{payrollResults.length}</p>
              <p className="text-xs text-muted-foreground">Employees</p>
            </div>
            
            <div className="text-center">
              <p className="text-lg font-semibold">{calculationStatus.warnings.length}</p>
              <p className="text-xs text-muted-foreground">Warnings</p>
            </div>
            
            <div className="text-center">
              <p className="text-lg font-semibold text-green-600">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: currencyCode,
                }).format(payrollResults.reduce((sum, r) => sum + r.calculation.totalGrossPay, 0))}
              </p>
              <p className="text-xs text-muted-foreground">Total Gross</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}